import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/shared/lib/api-client'
import { DURABLE_QUERY, LIVE_QUERY } from '@/shared/lib/query-profiles'
import type {
  LiveState,
  JudgeState,
  JudgeParticipation,
  MagicLinkResponse,
  AuditLogEntry,
  Pagination,
} from '@app/shared/types/entities'

export function useLiveState(competitionId: string) {
  return useQuery({
    queryKey: ['competitions', competitionId, 'live-state'],
    queryFn: () => apiClient.get<LiveState>(`/api/competitions/${competitionId}/live-state`),
    ...LIVE_QUERY,
    enabled: Boolean(competitionId),
  })
}

export function useJudgeState(competitionId: string) {
  return useQuery({
    queryKey: ['competitions', competitionId, 'judge-state'],
    queryFn: () => apiClient.get<JudgeState>(`/api/competitions/${competitionId}/judge-state`),
    ...DURABLE_QUERY,
    enabled: Boolean(competitionId),
  })
}

export function useSetActiveQuestion(competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      participationId,
      questionNumber,
    }: {
      participationId: string
      questionNumber: number | null
    }) =>
      apiClient.put(`/api/participations/${participationId}/active-question`, {
        questionNumber,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
    },
  })
}

export function useSkipQuestion(competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      participationId,
      questionNumber,
      skip,
    }: {
      participationId: string
      questionNumber: number
      skip: boolean
    }) =>
      apiClient.put(`/api/participations/${participationId}/skip-question`, {
        questionNumber,
        skip,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
    },
  })
}

export function useDisqualifyParticipation(competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (participationId: string) =>
      apiClient.post(`/api/participations/${participationId}/disqualify`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
    },
  })
}

export function useReinstateParticipation(competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (participationId: string) =>
      apiClient.post(`/api/participations/${participationId}/reinstate`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'live-state'],
      })
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-state'],
      })
    },
  })
}

export function useJudgeParticipations(competitionId: string) {
  return useQuery({
    queryKey: ['competitions', competitionId, 'judge-participations'],
    queryFn: async (): Promise<JudgeParticipation[]> => {
      const res = await apiClient.get<{ judgeParticipations: JudgeParticipation[] }>(
        `/api/competitions/${competitionId}/judge-participations`,
      )
      return res.judgeParticipations
    },
    enabled: Boolean(competitionId),
  })
}

export function useCreateJudgeParticipation(competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { judgeId: string; categoryIds: string[]; isSuperJudge?: boolean }) =>
      apiClient.post(`/api/competitions/${competitionId}/judge-participations`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-participations'],
      })
    },
  })
}

export function useUpdateJudgeParticipation(id: string, competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { isSuperJudge?: boolean; categoryIds?: string[] }) =>
      apiClient.patch(`/api/judge-participations/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-participations'],
      })
    },
  })
}

export function useDeleteJudgeParticipation(competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/judge-participations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-participations'],
      })
    },
  })
}

export function useGenerateMagicLink(competitionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (judgeParticipationId: string) =>
      apiClient.post<MagicLinkResponse>(
        `/api/judge-participations/${judgeParticipationId}/magic-link`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['competitions', competitionId, 'judge-participations'],
      })
    },
  })
}

export function useAuditLog(
  competitionId: string,
  params: { page?: number; pageSize?: number; entityType?: string; action?: string },
) {
  return useQuery({
    queryKey: ['competitions', competitionId, 'audit-log', params],
    queryFn: () =>
      apiClient.get<{ entries: AuditLogEntry[]; pagination: Pagination }>(
        `/api/competitions/${competitionId}/audit-log`,
        params,
      ),
    enabled: Boolean(competitionId),
  })
}
